import React from 'react';
import { useNavigate } from 'react-router-dom';
import TrendChart from './TrendChart';
import NineSystemsGrid from './NineSystemsGrid';
import '../styles/HealthSummaryCard.css';

interface HealthSummaryCardProps {
  onViewBodyOverview?: () => void;
}

const HealthSummaryCard: React.FC<HealthSummaryCardProps> = ({ onViewBodyOverview }) => {
  const navigate = useNavigate();
  
  const bloodLipidData = [
    { date: '2024-01', value: 5.2 },
    { date: '2024-02', value: 5.8 },
    { date: '2024-03', value: 6.1 },
    { date: '2024-04', value: 5.9 },
    { date: '2024-05', value: 5.6 },
    { date: '2024-06', value: 5.4 }
  ];

  const lungNoduleData = [
    { date: '2024-01', value: 3.2 },
    { date: '2024-02', value: 3.1 },
    { date: '2024-03', value: 3.3 },
    { date: '2024-04', value: 3.2 },
    { date: '2024-05', value: 3.0 },
    { date: '2024-06', value: 2.9 }
  ];

  const handleViewAllTrends = () => {
    // 跳转到全部指标趋势页面
    navigate('/all-trends');
  };

  const handleAppointment = (type: string) => {
    // 处理预约检查
    console.log(`预约${type}检查`);
  };

  return (
    <div className="health-summary-card">

      
      {/* 健康概况 - 整合摘要和九大系统 */}
      <div className="summary-section">
        <div className="summary-header">
          <h4>健康概况</h4>
          <span className="update-time-right">2024-06-15</span>
        </div>
        <div className="integrated-overview">
          <div className="overview-summary">
            <p>整体健康状况良好，需重点关注血脂水平偏高和肺部小结节变化。建议定期复查，保持健康的生活方式。</p>
          </div>
          {/* 九大系统模块 - 内联版 */}
          <NineSystemsGrid className="health-card-systems" />
        </div>
      </div>
      
      {/* 2. 重点指标趋势 */}
      <div className="trends-section">
        <h4>重点指标趋势</h4>
        <div className="charts-container">
          <div className="chart-item">
            <div className="chart-header">
              <h5>血脂水平 (mmol/L)</h5>
              <div className="chart-target-info">
                <span className="target-label">目标值: &lt; 5.2</span>
              </div>
            </div>
            <TrendChart 
              data={bloodLipidData} 
              color="#FF6B6B" 
              unit=" mmol/L"
              thresholdValue={5.2}
              thresholdLabel="正常上限"
            />
          </div>
          <div className="chart-item">
            <div className="chart-header">
              <h5>肺结节大小 (mm)</h5>
              <div className="chart-target-info">
                <span className="target-label">监测值: &lt; 5.0</span>
              </div>
            </div>
            <TrendChart 
              data={lungNoduleData} 
              color="#4ECDC4" 
              unit=" mm"
              thresholdValue={5.0}
              thresholdLabel="关注线"
            />
          </div>
        </div>
        
        {/* 全部指标趋势按钮 - 移到图表容器外面 */}
        <div className="trends-actions">
          <button className="view-all-trends-external" onClick={handleViewAllTrends}>
            全部指标趋势
            <span className="arrow-icon">→</span>
          </button>
        </div>
      </div>
      
      {/* 3. 风险问题 - 增加更多风险点 */}
      <div className="risks-section">
        <h4>风险评估</h4>
        <div className="risk-items-optimized">
          <div className="risk-item-primary">
            <div className="risk-content">
              <div className="risk-header-main">
                <h5 className="risk-title-main">心血管疾病风险</h5>
                <div className="risk-level medium">中等风险</div>
              </div>
              <p className="risk-description-main">
                10年心血管疾病风险约为12%，建议控制血脂水平，定期监测血压。
              </p>
              <div className="risk-points">
                <span className="risk-point">• 血脂偏高</span>
                <span className="risk-point">• 需监测血压</span>
                <span className="risk-point">• 建议生活方式干预</span>
              </div>
            </div>
          </div>
          
          <div className="risk-item-secondary">
            <div className="risk-content">
              <div className="risk-header-main">
                <h5 className="risk-title-main">肿瘤风险</h5>
                <div className="risk-level low">低风险</div>
              </div>
              <p className="risk-description-main">
                肺结节呈缩小趋势，恶性概率较低，建议6个月后复查CT。
              </p>
              <div className="risk-points">
                <span className="risk-point">• 结节呈缩小趋势</span>
                <span className="risk-point">• 恶性概率低</span>
                <span className="risk-point">• 定期影像学随访</span>
              </div>
            </div>
          </div>
          

        </div>
      </div>
      
      {/* 4. 推荐处理方式 - 优化预约按钮布局 */}
      <div className="recommendations-section">
        <h4>下一步最佳行动</h4>
        <div className="recommendation-items-clean">
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>血脂检查</h5>
                <p>控制血脂水平，降低心血管风险</p>
                <div className="check-schedule">
                  <span className="check-frequency">每3个月</span>
                  <span className="next-check">2025年10月</span>
                </div>
              </div>
              <span className="rec-reminder">添加提醒</span>
            </div>
          </div>
          
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>肺结节监测</h5>
                <p>定期CT检查，观察结节变化</p>
                <div className="check-schedule">
                  <span className="check-frequency">每6个月</span>
                  <span className="next-check">2025年12月</span>
                </div>
              </div>
              <span className="rec-reminder">添加提醒</span>
            </div>
          </div>
          
          <div className="recommendation-item-enhanced">
            <div className="rec-content-enhanced">
              <div className="rec-main-content">
                <h5>营养咨询</h5>
                <p>专业营养师指导，制定个性化饮食方案</p>
              </div>
              <button 
                className="call-doctor-btn"
                onClick={() => handleAppointment('营养咨询')}
              >
                呼叫医生
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* 5. 引用资料 - 弱化处理 */}
      <div className="references-section">
        <h4>引用资料</h4>
        <div className="reference-items">
          <div className="reference-item">
            <span>中国成人血脂异常防治指南(2023版)</span>
          </div>
          <div className="reference-item">
            <span>肺结节诊治中国专家共识(2022版)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthSummaryCard;